import { observer } from 'mobx-react-lite'
import { Autocomplete, Box, createFilterOptions, useTheme } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { useSkillAssessmentStore } from '../../skill-assessment/store/skill-assessment.store'
import { useEffect, useState } from 'react'
import { ProfessionFieldsType, SkillSet } from '../../skill-assessment/model/skill-assessment-model'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { ExpectedLevels } from './template/ExpectedLevels'
import { useNotificationStore } from 'src/feature/notification/store/notification.store'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { ModalWindow } from 'src/component/common/analog/template/ModalWindow'
import { Paragraph } from 'src/feature/localization/component/atom/Paragraph'
import { AppTextField } from 'src/feature/form/component/analog/AppTextField'
import { AppCircularRating } from 'src/component/common/analog/molecule/AppCircularRating'
import { AppDivider } from 'src/component/common/analog/molecule/AppDivider'

export type ExpectedRatingProps = {
  levelId: string
  disablePaper?: boolean
  onChange: () => void
}

const ROLE_OR_SKILL_NAME_MAX_LENGTH = 50
const PROFESSION_FIELD_MAX_LENGTH = 150

export const ExpectedRating = observer(({ levelId, disablePaper = false, onChange }: ExpectedRatingProps) => {
  const { translation } = useLocalizationStore()
  const translations = translation.pages.myTeam
  const skillAssessmentStore = useSkillAssessmentStore()
  const notificationStore = useNotificationStore()
  const theme = useTheme()
  const { isLoading: isSkillcategory } = skillAssessmentStore.getGenericFieldsState('getSkillCategory')
  const { isLoading: isDeleteExpectedRole, isError: isDeletedExpectedRoleError } = skillAssessmentStore.getGenericFieldsState('deleteExpectedRole')
  const { isLoading: isDeleteSkillSet, isError: isDeletedSkillSetError } = skillAssessmentStore.getGenericFieldsState('deleteSkillSet')
  const { isLoading: isAddSkillSetLoader } = skillAssessmentStore.getGenericFieldsState('addSkillSet')
  
  const isLoading = isSkillcategory || isDeleteExpectedRole || isDeleteSkillSet || isAddSkillSetLoader
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [skillAssessmentSetIndex, setSkillAssessmentSetIndex] = useState<number>(0)
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [isEditSkill, setIsEditSkill] = useState<boolean>(false)
  const [isAddSkillSet, setIsAddSkillSet] = useState<boolean>(false)
  const [skillSetTitle, setSkillSetTitle] = useState<string>('')
  const [professionField, setProfessionField] = useState<ProfessionFieldsType | null>(null)
  const filter = createFilterOptions<ProfessionFieldsType>()

  useEffect(() => {
    !skillAssessmentStore.professionsFieldsData?.length && skillAssessmentStore.getProfessionFields()
  }, [levelId])

  const handleProfessionFieldsChange = (_: any, newValue: ProfessionFieldsType | null) => {
    if (newValue && newValue.inputValue) {
      setProfessionField({
        id: newValue.id,
        title: newValue.inputValue,
      })
    } else {
      setProfessionField(newValue || null)
    }
  }

  const handleProfessionFieldsFilterChange = (options: Array<ProfessionFieldsType>, params: any) => {
    const filtered = filter(options, params)
    const { inputValue } = params
    const isExisting = options.some(option => inputValue === option.title)
    
    if (inputValue !== '' && !isExisting) {
      filtered.push({
        inputValue,
        id: `default-id-'${inputValue.toLowerCase()}'`,
        title: `Add '${inputValue}'`,
      })
    }

    return filtered
  }

  const handleRateSkillSetClick = (selectedSkillSetIndex: number) => {
    setSkillAssessmentSetIndex(selectedSkillSetIndex)
    setIsModalOpen(true)
  }

  const handleDeleteExpectedRole = async () => {
    skillAssessmentStore.deleteExpectedRole(levelId).then(() => {
      if (!isDeletedExpectedRoleError) {
        onChange()
      } else {
        notificationStore.enqueueError(translation.util.unknownErrorOccurred())
      }
    })
  }

  const handleDeleteSkillSet = (skillSet: SkillSet) => {
    skillAssessmentStore.deleteSkillSet(skillSet.id, skillSet.categoryId).then(() => {
      if (!isDeletedSkillSetError) {
        skillAssessmentStore.getSkillCategory(levelId)
      } else {
        notificationStore.enqueueError(translation.util.unknownErrorOccurred())
      }
    })
  }

  const skillSetTitleChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSkillSetTitle(event.target.value)
  }

  const handleAddSkillSet = async () => {
    const titleRegex = /^.{1,50}$/
    const professionFieldRegex = /^.{1,150}$/

    if (professionField && professionFieldRegex.test(professionField.title) && skillSetTitle && titleRegex.test(skillSetTitle)) {
      if (skillAssessmentStore.skillCategory) {
        setIsAddSkillSet(false)
        setSkillSetTitle('')
        try {
          await skillAssessmentStore.addSkillSet(professionField.title, skillSetTitle, skillAssessmentStore.skillCategory?.id)
          notificationStore.enqueueSuccess(translations.errorMsg.changesSavedSuccessfully())
          skillAssessmentStore.getSkillCategory(levelId)
        } catch (error) {
          notificationStore.enqueueError(translation.util.unknownErrorOccurred())
        }
      } else {
        notificationStore.enqueueError(translation.util.unknownErrorOccurred())
      }
    } else {
      notificationStore.enqueueError(translations.errorMsg.roleError())
    }
  }

  const checkIfAllSkillsAreRated = () => {
    setIsModalOpen(false)
  }

  return (
    <AppBox sx={styles.root}>
      {!isLoading && skillAssessmentStore.skillCategory && <AppBox sx={styles.ratingWrapper}>
        {!isLoading && skillAssessmentStore.skillCategory && skillAssessmentStore.skillCategory?.skills?.length !== 0 && skillAssessmentStore.skillCategory?.skills?.map((skill: SkillSet, index: number) => (
          <AppCircularRating
            size={140}
            key={skill.id}
            indexKey={index}
            expectedRating={skill.expected_set_level}
            label={skill.setName}
            showLevelBtn={true}
            onRateSkillClick={handleRateSkillSetClick}
            showRemoveBtn={isEditSkill}
            onRemoveSkillClick={() => handleDeleteSkillSet(skill)}
            viewLabel='ManagerView'
            defaultColor={disablePaper ? theme.palette.background.paper : theme.palette.background.dark}
          />
        ))}
        {skillAssessmentStore.skillCategory && skillAssessmentStore.skillCategory?.skills?.length !== 30 && isEditSkill && <Box
          sx={{
            ...styles.progressWrapper,
            backgroundColor: disablePaper ? theme.palette.background.paper : theme.palette.background.dark,
          }}
        >
          <Caption
            variant="caption"
            component="div"
            sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <AppButton
              fontSize={10}
              variant="dark"
              textValue={translation.pages.myUnusedSkills.addSkillBtn()}
              onClick={() => setIsAddSkillSet(true)}
            ></AppButton>
          </Caption>
        </Box>}
      </AppBox>}
      {!isLoading && skillAssessmentStore.skillCategory && <AppBox sx={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
        <AppBox sx={{
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
        }}>
          {!isLoading && skillAssessmentStore.skillCategory && !isEditSkill && (
            <AppButton
              textValue={translations.rolesButton.updateSkill()}
              fontSize={14}
              variant='contained'
              sx={{ width: '10em' }}
              onClick={() => setIsEditSkill(!isEditSkill)}
            />
          )}
          {!isLoading && skillAssessmentStore.skillCategory && isEditSkill && (
            <AppButton
              textValue={translations.rolesButton.finishButton()}
              fontSize={14}
              variant='light'
              onClick={() => setIsEditSkill(!isEditSkill)}
            />
          )}
          {!isLoading && skillAssessmentStore.skillCategory && isEditSkill && (
            <AppButton
              variant="danger"
              textValue={translations.rolesButton.deleteRole()}
              fontSize={14}
              onClick={() => setShowConfirmDelete(true)}
            />
          )}
        </AppBox>
      </AppBox>}
      {isModalOpen && <ExpectedLevels skillAssessmentSetIndex={skillAssessmentSetIndex} isModalOpen={isModalOpen} isModalClose={checkIfAllSkillsAreRated} />}
      {showConfirmDelete && <ModalWindow
        modalProps={{
          open: showConfirmDelete,
          onClose: () => setShowConfirmDelete(false),
          PaperProps: {
            sx: {
              maxWidth: { xs: '90%', md: '60%', lg: '30%' },
              borderRadius: '10px',
            },
          },
        }}
      >
        <AppBox sx={{ flexDirection: 'column', gap: 10 }}>
          <Paragraph textValue={translations.deleteRoleConfirmation()} />
          <AppDivider />
          <AppBox sx={{ gap: 10, justifyContent: 'flex-end' }}>
            <AppButton
              fontSize={12}
              textValue={translation.buttons.cancel}
              variant={'light'}
              onClick={() => setShowConfirmDelete(false)}
            />
            <AppButton
              variant="danger"
              fontSize={12}
              textValue={translation.buttons.confirm}
              onClick={handleDeleteExpectedRole}
            />
          </AppBox>
        </AppBox>
      </ModalWindow>}
      {isAddSkillSet && <ModalWindow
        modalProps={{
          open: isAddSkillSet,
          onClose: () => setIsAddSkillSet(false),
          PaperProps: {
            sx: {
              maxWidth: { xs: '90%', md: '60%', lg: '30%' },
              borderRadius: '10px',
            },
          },
        }}
        titleProps={{ textValue: translation.pages.editPositions.addPositionButton() }}
      >
        <AppBox sx={{ flexDirection: 'column', gap: 10 }}>
          <AppTextField
            sx={{
              fontSize: 12,
              m: 0,
              width: '100%',
              '& .MuiInputBase-formControl': {
                height: 38,
              },
            }}
            defaultValue={skillSetTitle}
            placeholder={translations.setSkillPlaceholder()}
            onChange={skillSetTitleChanged}
            inputProps={{ maxLength: ROLE_OR_SKILL_NAME_MAX_LENGTH }}
          />
          <Autocomplete
            sx={{
              width: '100%',
              '& .MuiInputBase-formControl': {
                height: 38,
              },
              '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
                color: 'text.primary',
              },
              '& .MuiChip-label': {
                fontSize: 12,
              },
            }}
            value={professionField}
            onChange={handleProfessionFieldsChange}
            filterOptions={handleProfessionFieldsFilterChange}
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            id='professionField'
            size='small'
            options={skillAssessmentStore.professionsFieldsData || []}
            getOptionLabel={option => option.title || ''}
            renderOption={(props, option) => (
              <li {...props}>
                {option.title}
              </li>
            )}
            renderInput={params => (
              <AppTextField
                {...params}
                placeholder={translations.setDemandFieldPlaceholder()}
                sx={{
                  fontSize: 12,
                  m: 0,
                }}
                inputProps={{
                  ...params.inputProps,
                  maxLength: PROFESSION_FIELD_MAX_LENGTH,
                }}
              />
            )}
          />
          <AppDivider />
          <AppBox sx={{ gap: 10, justifyContent: 'flex-end' }}>
            <AppButton
              fontSize={12}
              textValue={translation.buttons.cancel}
              variant={'light'}
              onClick={() => setIsAddSkillSet(false)}
            />
            <AppButton
              fontSize={12}
              textValue={translation.buttons.confirm}
              variant="contained"
              disabled={!skillSetTitle}
              onClick={handleAddSkillSet}
            />
          </AppBox>
        </AppBox>
      </ModalWindow>}
    </AppBox>
  )
})

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  progressWrapper: {
    display: 'flex',
    alignSelf: 'center',
    width: '140px',
    height: '140px',
    borderRadius: '50%',
    transition: 'all 0.3s',
    '&:hover': {
      cursor: 'pointer',
      transform: 'scale(1.05)',
    },
  },
  ratingWrapper: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    justifyContent: 'center',
    rowGap: 20,
    columnGap: 40,
    width: '100%',
    maxWidth: 'calc(5 * 160px + 4 * 40px)',
    margin: '0 auto',
  },
}